/**
 * Single source of truth for Assets Excel import/export.
 * Sheet names and column definitions (key = payload field, header = Excel column).
 * Import: Excel row[header] → payload[key]. Export: payload[key] → row[header].
 */

export const SHEET_NAMES = {
  LOCATIONS: 'Locations',
  CATEGORIES: 'Categories',
  ASSETS: 'Assets',
  OPERATIONS: 'Operations',
  RETURNS: 'Returns',
  SNAPSHOTS: 'Snapshots',
  SNAPSHOT_ITEMS: 'Balances',
};

/** Payload keys in order (same as import/export contract). */
export const PAYLOAD_KEYS = [
  'locations',
  'categories',
  'assets',
  'operations',
  'returns',
  'snapshots',
  'snapshot_items',
];

/** Map payload key → sheet name. */
export const PAYLOAD_KEY_TO_SHEET = {
  locations: SHEET_NAMES.LOCATIONS,
  categories: SHEET_NAMES.CATEGORIES,
  assets: SHEET_NAMES.ASSETS,
  operations: SHEET_NAMES.OPERATIONS,
  returns: SHEET_NAMES.RETURNS,
  snapshots: SHEET_NAMES.SNAPSHOTS,
  snapshot_items: SHEET_NAMES.SNAPSHOT_ITEMS,
};

/** Column definitions per payload key: { key, header } in Excel order. */
export const SHEET_COLUMNS = {
  locations: [
    { key: 'location_name', header: 'location_name' },
    { key: 'type', header: 'type' },
    { key: 'is_active', header: 'is_active' },
  ],
  categories: [
    { key: 'category_code', header: 'category_code' },
    { key: 'order', header: 'order' },
    { key: 'level_0', header: 'level_0' },
    { key: 'level_1', header: 'level_1' },
    { key: 'level_2', header: 'level_2' },
    { key: 'level_3', header: 'level_3' },
  ],
  assets: [
    { key: 'asset_code', header: 'asset_code' },
    { key: 'asset_counter', header: 'asset_counter' },
    { key: 'asset_name', header: 'asset_name' },
    { key: 'asset_type', header: 'asset_type' },
    { key: 'category_code', header: 'category_code' },
    { key: 'location_name', header: 'location_name' },
    { key: 'external_ticker', header: 'external_ticker' },
    { key: 'is_updated', header: 'is_updated' },
    { key: 'is_active', header: 'is_active' },
    { key: 'update_mode', header: 'update_mode' },
  ],
  operations: [
    { key: 'asset_code', header: 'asset_code' },
    { key: 'operation_status', header: 'operation_status' },
    { key: 'operation_date', header: 'operation_date' },
    { key: 'redeem_date', header: 'redeem_date' },
    { key: 'quantity', header: 'quantity' },
    { key: 'total_value', header: 'total_value' },
    { key: 'net_value', header: 'net_value' },
    { key: 'rate_description', header: 'rate_description' },
    { key: 'notes', header: 'notes' },
  ],
  returns: [
    { key: 'asset_code', header: 'asset_code' },
    { key: 'payment_type', header: 'payment_type' },
    { key: 'payment_date', header: 'payment_date' },
    { key: 'payment_value', header: 'payment_value' },
    { key: 'quantity_at_time', header: 'quantity_at_time' },
    { key: 'notes', header: 'notes' },
  ],
  snapshots: [
    { key: 'snapshot_date', header: 'snapshot_date' },
    { key: 'is_closing', header: 'is_closing' },
    { key: 'notes', header: 'notes' },
  ],
  snapshot_items: [
    { key: 'asset_code', header: 'asset_code' },
    { key: 'snapshot_source', header: 'snapshot_source' },
    { key: 'snapshot_date', header: 'snapshot_date' },
    { key: 'snapshot_value', header: 'snapshot_value' },
    { key: 'snapshot_quantity', header: 'snapshot_quantity' },
    { key: 'market_unitary_value', header: 'market_unitary_value' },
    { key: 'market_source', header: 'market_source' },
  ],
};

export function getSheetName(payloadKey) {
  return PAYLOAD_KEY_TO_SHEET[payloadKey] || payloadKey;
}

export function getColumns(payloadKey) {
  return SHEET_COLUMNS[payloadKey] || [];
}

/**
 * Build an XLSX workbook from the export payload (same shape as import).
 * @param {object} payload - { locations, categories, assets, operations, returns, snapshots, snapshot_items }
 * @param {object} XLSX - the xlsx library (e.g. import * as XLSX from 'xlsx')
 * @returns {object} workbook - use XLSX.writeFile(workbook, filename) to download
 */
export function buildWorkbookFromPayload(payload, XLSX) {
  const wb = XLSX.utils.book_new();
  for (const payloadKey of PAYLOAD_KEYS) {
    const list = payload[payloadKey];
    if (!Array.isArray(list) || list.length === 0) continue;
    const columns = getColumns(payloadKey);
    const rows = list.map((item) => {
      const row = {};
      for (const col of columns) {
        let val = item[col.key];
        if (val === null || val === undefined) val = '';
        if (typeof val === 'boolean') val = val ? 'true' : 'false';
        if (typeof val === 'object' && val !== null && !(val instanceof Date)) {
          val = JSON.stringify(val);
        }
        if (val instanceof Date) val = val.toISOString().split('T')[0];
        row[col.header] = val;
      }
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const sheetName = getSheetName(payloadKey);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  }
  return wb;
}
